import { Dispatch, SetStateAction, useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ReactComponent as MessageIcon } from "../../../../assets/Profile/specialist/message.svg";
import CircleAvatar from "../../../../components/CircleAvatar/CircleAvatar";
import Input from "../../../../components/Input/Input";
import MultipleSelect from "../../../../components/MultipleSelect/MultipleSelect";
import { ContextProvider } from "../../../../contextProvider";
import { FlexPassSubscription } from "../../../../types/FlexPassTypes";
import { GeneralMultiLangText } from "../../../../types/TypeUsers";
import { formatDateAndTime } from "../../../../utils/dateActions";
import { getTranslation } from "../../../../utils/getTranslation";
import s from "./FlexPassInviteStatistic.module.css";

type InviteStatisticProps = {
  entryCodes: {
    code: string;
    forSpecialistUser: {
      _id: string;
      name: GeneralMultiLangText[];
      surname: GeneralMultiLangText[];
    };
    forSpecialist: string;
    isAccepted: boolean;
    isRejected: boolean;
    sentAt: Date;
    answeredAt?: Date;
  }[];
  flexPassInfo: FlexPassSubscription;
  setModalIsOpen: Dispatch<SetStateAction<boolean>>;
  setInfoModalIsOpen: Dispatch<SetStateAction<boolean>>;
};

const getStatus = (isAccepted: boolean, isRejected: boolean) => {
  if (isAccepted) {
    return {
      className: s.acceptStatus,
      title: "Accepted",
    };
  }
  if (isRejected) {
    return {
      className: s.rejectStatus,
      title: "Rejected",
    };
  }
  if (!isAccepted && !isRejected) {
    return {
      className: s.awaitStatus,
      title: "Not answered",
    };
  }
};

const FlexPassInviteStatistic = ({
  entryCodes,
  setModalIsOpen,
  setInfoModalIsOpen,
  flexPassInfo,
}: InviteStatisticProps) => {
  const { setUserIdForCreate } = useContext(ContextProvider);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const filteredData = useMemo(() => {
    return entryCodes
      .filter((row) => {
        const fullName =
          getTranslation(row.forSpecialistUser.name, flexPassInfo.language) +
          " " +
          getTranslation(row.forSpecialistUser.surname, flexPassInfo.language);
        const matchesSearch = fullName
          .toLowerCase()
          .includes(search.toLowerCase());

        const status = getStatus(row.isAccepted, row.isRejected)?.title;
        const matchesStatus = statusFilter === "All" || status === statusFilter;

        return matchesSearch && matchesStatus;
      })
      .sort(
        (a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime()
      );
  }, [entryCodes, search, statusFilter]);

  const filterStatusData = [
    {
      lable: "All",
      value: "All",
    },
    {
      lable: "Accepted",
      value: "Accepted",
    },
    {
      lable: "Rejected",
      value: "Rejected",
    },
    {
      lable: "Not answered",
      value: "Not answered",
    },
  ];

  return (
    <div className={s.container}>
      <span className={s.filtersTitle}>Filters</span>
      <div className={s.filters}>
        <Input
          inputValue={search}
          onChangeInput={(value) => setSearch(value)}
          isVisible
          required
          placeholder="Search by name or email"
          width="25%"
        />
        <MultipleSelect
          data={filterStatusData}
          multiplie={false}
          setValue={(value) => setStatusFilter(value as string)}
          value={statusFilter}
          disableSortData
          width="25%"
        />
      </div>
      <div className={s.tableBlock}>
        <table className={s.table}>
          <thead>
            <tr className={s.headerRow}>
              <th>Name</th>
              <th>Sent Date</th>
              <th>Answer Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredData && filteredData.length > 0 ? (
              filteredData.map((row, i) => (
                <tr key={i} className={s.row}>
                  <td>
                    <div className={s.nameAvatarBlock}>
                      <CircleAvatar userId={row.forSpecialistUser._id} />
                      <span>
                        {getTranslation(
                          row.forSpecialistUser.name,
                          flexPassInfo.language
                        ) +
                          " " +
                          getTranslation(
                            row.forSpecialistUser.surname,
                            flexPassInfo.language
                          )}
                      </span>
                    </div>
                  </td>

                  <td>
                    {
                      formatDateAndTime(new Date(row.sentAt), "en")
                        .formattedDate
                    }
                    ,{" "}
                    {
                      formatDateAndTime(new Date(row.sentAt), "en")
                        .formattedTime
                    }
                  </td>
                  <td>
                    {row.answeredAt &&
                      formatDateAndTime(new Date(row.sentAt), "en")
                        .formattedDate +
                        ", " +
                        formatDateAndTime(new Date(row.sentAt), "en")
                          .formattedTime}
                  </td>
                  <td>
                    <div className={s.statusBlock}>
                      <div
                        className={`${s.status} ${
                          getStatus(row.isAccepted, row.isRejected)?.className
                        }`}
                      >
                        {getStatus(row.isAccepted, row.isRejected)?.title}
                      </div>
                      <Link
                        to="/chat"
                        onClick={() => {
                          setUserIdForCreate(row.forSpecialistUser._id);
                          setModalIsOpen(false);
                          setInfoModalIsOpen(false);
                        }}
                        className={s.sendMessageBtn}
                      >
                        {" "}
                        <MessageIcon />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className={s.emptyState}>
                  Invitations not found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FlexPassInviteStatistic;
